import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authApi } from "../../api/authApi";
import { getStoredToken, setStoredToken } from "../../api/http";

export const loginThunk = createAsyncThunk(
  "auth/login",
  async (payload, thunkApi) => {
    try {
      const data = await authApi.login(payload);
      return data; // pode ser {success, data:user} ou {token, user}
    } catch (e) {
      return thunkApi.rejectWithValue({
        message: e.message,
        status: e.status,
        data: e.data,
      });
    }
  },
);

export const logoutThunk = createAsyncThunk(
  "auth/logout",
  async (_, thunkApi) => {
    try {
      const data = await authApi.logout(); // POST /auth/logout
      return data;
    } catch (e) {
      // mesmo se falhar, a gente limpa o state local pra não ficar preso
      return thunkApi.rejectWithValue({ message: e.message });
    }
  },
);

export const meThunk = createAsyncThunk("auth/me", async (_, thunkApi) => {
  try {
    const data = await authApi.me();
    return data; // pode ser {success, data:user} ou {user}
  } catch (e) {
    return thunkApi.rejectWithValue({
      message: e.message,
      status: e.status,
      data: e.data,
    });
  }
});

const initialState = {
  token: getStoredToken(),
  user: null,
  status: "idle",
  error: null,
  hasCheckedAuth: false,
};

function extractUser(payload) {
  // backend atual: { success:true, data: { ...user } }
  // alternativa: { user: {...} }
  return payload?.user ?? payload?.data ?? payload?.data?.user ?? null;
}

function extractToken(payload) {
  // se algum dia voltar token: { token } ou { data: { token } }
  return payload?.token ?? payload?.data?.token ?? null;
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.user = null;
      state.error = null;
      state.status = "idle";
      setStoredToken(null);
    },
    clearAuthError(state) {
      state.error = null;
    },
    setToken(state, action) {
      state.token = action.payload || null;
      if (state.token) setStoredToken(state.token);
      else setStoredToken(null);
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGIN
      .addCase(loginThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.status = "succeeded";

        const user = extractUser(action.payload);
        const token = extractToken(action.payload);

        state.user = user;

        // cookie-based: token provavelmente será null → não salva
        state.token = token || state.token || null;

        if (token) setStoredToken(token);

        state.hasCheckedAuth = true;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
        // garante que não fica "meio logado"
        state.user = null;
        state.token = null;
        setStoredToken(null);
        state.hasCheckedAuth = true;
      })

      // ME (sessão via cookie)
      .addCase(meThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(meThunk.fulfilled, (state, action) => {
        state.status = "succeeded";

        const user = extractUser(action.payload);
        const token = extractToken(action.payload);

        state.user = user;

        // se existir token, salva; se não, mantém o que já tinha (ou null)
        if (token) {
          state.token = token;
          setStoredToken(token);
        }

        state.hasCheckedAuth = true;
      })
      .addCase(meThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;

        // sessão inválida/expirada → limpa
        state.user = null;
        state.token = null;
        setStoredToken(null);

        state.hasCheckedAuth = true;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.token = null;
        state.user = null;
        state.error = null;
        state.status = "idle";
        state.hasCheckedAuth = true;
        setStoredToken(null);
      })
      .addCase(logoutThunk.rejected, (state) => {
        // ainda assim limpa local
        state.token = null;
        state.user = null;
        state.error = null;
        state.status = "idle";
        state.hasCheckedAuth = true;
        setStoredToken(null);
      });
  },
});

export const { logout, clearAuthError, setToken } = authSlice.actions;
export default authSlice.reducer;
